import {
  Image,
  Text,
  Anchor,
  Button,
  Stack,
  Space,
  Title,
} from '@mantine/core';
import { IconMailFilled } from '@tabler/icons-react';
import { motion } from 'motion/react';
import { LoginWrapper } from '../LoginWrapper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageRoutes } from '../../constants/routes';
import { primaryColor } from '../../constants/theme';
import { GoogleSignOnButton } from './Components/GoogleSignOnButton';
import AppleSignInButton from './Components/AppleSignOnButton';
import { useCallback, useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { LocalStorageKeys } from '../../constants/locationStorage';
import { useAuth } from '../../auth/AuthContext';

const ALREADY_HAVE_ACCOUNT = 'ACCOUNT_EXISTS_OTHER_PROVIDER';

const TermsAndLoginText = ({ returnTo }: { returnTo?: string | null }) => {
  const navigate = useNavigate();
  const loginPath = returnTo
    ? `${PageRoutes.EMAIL_LOGIN}?returnTo=${encodeURIComponent(returnTo)}`
    : PageRoutes.EMAIL_LOGIN;

  return (
    <>
      <Text ta="center">
        By continuing you agree to CircleBe's{' '}
        <Anchor fw="bold" href="https://www.circlebe.com/terms-and-conditions">
          Terms and Conditions
        </Anchor>{' '}
        and{' '}
        <Anchor href="https://www.circlebe.com/privacy-policy" fw="bold">
          Privacy Policy
        </Anchor>
      </Text>
      <HaveAnAccountAlreadyText returnTo={returnTo} />
    </>
  );
};

export const HaveAnAccountAlreadyText = ({
  returnTo,
}: {
  returnTo?: string | null;
}) => {
  const navigate = useNavigate();
  const loginPath = returnTo
    ? `${PageRoutes.EMAIL_LOGIN}?returnTo=${encodeURIComponent(returnTo)}`
    : PageRoutes.EMAIL_LOGIN;

  return (
    <Text ta="center">
      Have an account already?{' '}
      <Anchor fw={'bold'} onClick={() => navigate(loginPath)}>
        Log In
      </Anchor>
    </Text>
  );
};

export const SingleSignOnOptions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alreadyExists, setAlreadyExists] = useState(false);
  const { exchangeGoogleApple } = useAuth();
  const { getProfileAndRedirect } = useApp();

  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    if (returnTo) {
      localStorage.setItem(LocalStorageKeys.POST_LOGIN_REDIRECT, returnTo);
      return;
    }

    localStorage.removeItem(LocalStorageKeys.POST_LOGIN_REDIRECT);
  }, [returnTo]);

  const onIdToken = useCallback(
    async (idToken: string, provider: string, nonce?: string) => {
      alreadyExists && setAlreadyExists(false);
      try {
        const session = await exchangeGoogleApple(
          idToken,
          provider.toLowerCase() as 'google' | 'apple',
          nonce
        );

        // Check for email verification (skip for Apple as email is auto-confirmed)
        if (!session.emailConfirmed && provider.toLowerCase() !== 'apple') {
          navigate(PageRoutes.EMAIL_SIGNUP_VERIFY);
          return;
        }

        // Check for profile completion
        if (!session.profileConfirmed) {
          navigate(PageRoutes.ONBOARDING_BEST_DESCRIBES);
          return;
        }

        // Use the access token directly from the session to avoid a render-cycle race condition
        await getProfileAndRedirect(session.accessToken, returnTo);
      } catch (error: any) {
        console.error('SSO authentication failed:', error);

        // Check for account already exists with another provider
        if (error?.response?.data?.code === ALREADY_HAVE_ACCOUNT) {
          setAlreadyExists(true);
        }
      }
    },
    [exchangeGoogleApple, navigate, alreadyExists, getProfileAndRedirect, returnTo]
  );

  return (
    <LoginWrapper animate={false}>
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1.5, x: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      >
        <Stack>
          <Stack hiddenFrom="md">
            <Image src="/logo_talk2me.png" alt="CircleBe Logo" maw={200} mx="auto" />
          </Stack>
          <Stack visibleFrom="md" gap={0}>
            <Title ta="center" c="black">
              Welcome Back
            </Title>
            <Text ta="center" c="black">
              Continue and get connected
            </Text>
          </Stack>

          <Space h="md"></Space>
          <GoogleSignOnButton
            onIdToken={(idToken) => onIdToken(idToken, 'Google')}
          ></GoogleSignOnButton>
          <AppleSignInButton
            onIdToken={(idToken, nonce) => onIdToken(idToken, 'Apple', nonce)}
          ></AppleSignInButton>
          <Button
            color={primaryColor}
            radius={'lg'}
            size={'md'}
            type="submit"
            leftSection={<IconMailFilled />}
            onClick={() => {
              const path = returnTo
                ? `${PageRoutes.EMAIL_SIGNUP}?returnTo=${encodeURIComponent(returnTo)}`
                : PageRoutes.EMAIL_SIGNUP;
              navigate(path);
            }}
          >
            Continue with Email
          </Button>
          <Text c="red">
            {alreadyExists
              ? 'User already has an account with another provider'
              : ''}
          </Text>
          <Space pt="md"></Space>
          <TermsAndLoginText returnTo={returnTo} />
        </Stack>
      </motion.div>
    </LoginWrapper>
  );
};
